/**
 * Separators that go between modules on content library pages.
 *
 * The main user of these separators is ModuleList, which puts them in between
 * modules.  But a few modules (as I write this, just ProjectsInProgress)
 * include their own separators, so as to have more control over where and when
 * to show them.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@repo/shared-styles";

import {
    moduleHorizontalPadding,
    moduleHorizontalPaddingSmall,
    moduleLayout,
    moduleVerticalPaddingCompactModifer,
} from "./styles.ts";

type SeparatorProps = {
    extraClasses?: string; // space-separated css class names
};

export class Separator extends React.Component<SeparatorProps> {
    render(): React.ReactNode {
        let classes = css(styles.separator);
        if (this.props.extraClasses) {
            classes += " " + this.props.extraClasses;
        }
        return <div className={classes} aria-hidden={true} />;
    }
}

type NarrowSeparatorProps = {
    alignment: "center" | "right";
};

export class NarrowSeparator extends React.Component<NarrowSeparatorProps> {
    render(): React.ReactNode {
        return (
            <div
                className={
                    // For some reason we use compress for right, but not center.
                    this.props.alignment === "center"
                        ? css(styles.centered)
                        : css(styles.right, styles.compress)
                }
            >
                <div
                    className={css(styles.separator, styles.narrow)}
                    aria-hidden={true}
                />
            </div>
        );
    }
}

export class CompressedSeparator extends React.Component<Record<any, any>> {
    render(): React.ReactNode {
        return <div className={css(styles.compress)} />;
    }
}

export class Spacer extends React.Component<Record<any, any>> {
    render(): React.ReactNode {
        return (
            <div>
                <div className={css(styles.mobileOnlySeparator)} />
            </div>
        );
    }
}

const styles = StyleSheet.create({
    compress: {
        // HACK(mdr): Not only do we use this style to support "compact mode",
        //     but we also use it to shrink the spacing around ContentList
        //     separator lines, for consistency with the topic page design.
        //
        //     We could instead reduce the padding around ContentList modules,
        //     but the two-column large-screen topic page layout requires that
        //     the first module of the second column have a top padding of
        //     48px, in order to align correctly with the FloatingSidebar. We
        //     _could_ use getSeparator to inject an additional spacer element
        //     if a ContentList appears immediately after a FloatingSidebar,
        //     but then we'll have to do something similar for _any_ module
        //     with nonstandard padding, which decreases the plug-n-play power
        //     of Bibliotron modules by requiring us to explicitly think about
        //     all the contexts in which a new module could ever appear.
        //     Instead, this implementation contains the hack to just
        //     ContentList modules, rather than affecting all future modules.
        //
        //     This hack is a hint that our abstractions for Bibliotron module
        //     spacing are broken. As this case demonstrates, our designs call
        //     for the ability to change the padding around a module according
        //     to context, but our modules don't currently provide that power.
        //
        //     One solution might be to remove padding from all Bibliotron
        //     modules, and instead create new layout modules that wrap child
        //     modules and manage the spacing between them. That is, the
        //     padding around a ContentList is the same as the padding around
        //     any other module (that is, zero), and the topic page puts all
        //     its ContentList modules inside a parent layout module that
        //     inserts 32px of space and a horizontal ruler between all modules
        //     it contains.

        // Vertically center this element's contents within that gap.
        position: "relative",
        top: -moduleVerticalPaddingCompactModifer,
    },
    separator: {
        backgroundColor: color.offBlack16,
        height: 1,
    },
    mobileOnlySeparator: {
        [mediaQueries.smOrSmaller]: {
            backgroundColor: color.offBlack16,
            height: 1,
        },
    },
    centered: {
        ...moduleLayout.defaultAlignment,
    },
    right: {
        ...moduleLayout.rightAlignment,
    },
    narrow: {
        marginLeft: moduleHorizontalPadding,
        marginRight: moduleHorizontalPadding,

        [mediaQueries.smOrSmaller]: {
            marginLeft: moduleHorizontalPaddingSmall,
            marginRight: moduleHorizontalPaddingSmall,
        },
    },
});
